// src/utils/firebaseTest.js
// Comprehensive Firebase connection testing utility

import { auth, db, storage, isFirebaseInitialized } from '../config/firebase-config';
import {
  signInAnonymously,
  signOut as firebaseSignOut
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';

/**
 * Firebase Connection Test Suite
 * Tests all three Firebase services: Auth, Firestore, Storage
 * Returns detailed results for each test
 */

export const runFirebaseTests = async () => {
  const results = {
    timestamp: new Date().toISOString(),
    initialization: { success: false, message: '', details: null },
    auth: { success: false, message: '', details: null },
    firestore: { success: false, message: '', details: null },
    storage: { success: false, message: '', details: null },
    overall: { success: false, message: '' }
  };

  try {
    // ============================================
    // TEST 1: Firebase Initialization
    // ============================================
    console.log('🧪 Test 1: Checking Firebase initialization...');

    if (!isFirebaseInitialized()) {
      results.initialization.success = false;
      results.initialization.message = 'Firebase failed to initialize';
      results.overall.success = false;
      results.overall.message = 'Firebase not initialized - check firebase-config.js';
      return results;
    }

    if (!auth || !db || !storage) {
      results.initialization.success = false;
      results.initialization.message = 'One or more Firebase services are null';
      results.initialization.details = {
        auth: auth ? 'OK' : 'NULL',
        firestore: db ? 'OK' : 'NULL',
        storage: storage ? 'OK' : 'NULL'
      };
      results.overall.success = false;
      results.overall.message = 'Firebase services not available';
      return results;
    }

    results.initialization.success = true;
    results.initialization.message = 'Firebase initialized successfully';
    results.initialization.details = {
      auth: 'OK',
      firestore: 'OK',
      storage: 'OK'
    };
    console.log('✅ Firebase initialization passed');

    // ============================================
    // TEST 2: Firebase Auth
    // ============================================
    console.log('🧪 Test 2: Testing Firebase Auth...');

    try {
      // Sign in anonymously (doesn't require credentials)
      const userCredential = await signInAnonymously(auth);
      const user = userCredential.user;

      results.auth.success = true;
      results.auth.message = 'Successfully authenticated with Firebase Auth';
      results.auth.details = {
        uid: user.uid,
        isAnonymous: user.isAnonymous,
        authDomain: auth.config.authDomain
      };
      console.log('✅ Firebase Auth test passed:', user.uid);

      // Clean up: sign out
      await firebaseSignOut(auth);
      console.log('✅ Test user signed out');

    } catch (authError) {
      results.auth.success = false;
      results.auth.message = `Auth failed: ${authError.message}`;
      results.auth.details = {
        code: authError.code,
        message: authError.message
      };
      console.error('❌ Firebase Auth test failed:', authError);
    }

    // ============================================
    // TEST 3: Firestore Database
    // ============================================
    console.log('🧪 Test 3: Testing Firestore...');

    try {
      const testDocId = `test_${Date.now()}`;
      const testDocRef = doc(db, '_connection_tests', testDocId);

      // Write test document
      const testData = {
        test: true,
        timestamp: serverTimestamp(),
        message: 'Firebase connection test',
        createdBy: 'firebaseTest.js'
      };

      await setDoc(testDocRef, testData);
      console.log('✅ Firestore write successful');

      // Read test document
      const docSnap = await getDoc(testDocRef);

      if (!docSnap.exists()) {
        throw new Error('Document was written but could not be read back');
      }

      const readData = docSnap.data();
      console.log('✅ Firestore read successful');

      // Delete test document (cleanup)
      await deleteDoc(testDocRef);
      console.log('✅ Firestore delete successful (cleanup)');

      results.firestore.success = true;
      results.firestore.message = 'Firestore read/write/delete successful';
      results.firestore.details = {
        collection: '_connection_tests',
        documentId: testDocId,
        dataWritten: testData,
        dataRead: readData,
        operations: ['write', 'read', 'delete']
      };

    } catch (firestoreError) {
      results.firestore.success = false;
      results.firestore.message = `Firestore failed: ${firestoreError.message}`;
      results.firestore.details = {
        code: firestoreError.code || 'unknown',
        message: firestoreError.message
      };
      console.error('❌ Firestore test failed:', firestoreError);
    }

    // ============================================
    // TEST 4: Firebase Storage
    // ============================================
    console.log('🧪 Test 4: Testing Firebase Storage...');

    try {
      const testFileName = `test_${Date.now()}.txt`;
      const storageRef = ref(storage, `_connection_tests/${testFileName}`);

      // Create a small test file (blob)
      const testContent = 'Firebase Storage connection test';
      const testBlob = new Blob([testContent], { type: 'text/plain' });

      // Upload test file
      const uploadResult = await uploadBytes(storageRef, testBlob);
      console.log('✅ Storage upload successful');

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      console.log('✅ Storage URL generation successful');

      // Delete test file (cleanup)
      await deleteObject(storageRef);
      console.log('✅ Storage delete successful (cleanup)');

      results.storage.success = true;
      results.storage.message = 'Storage upload/download/delete successful';
      results.storage.details = {
        bucket: storage.app.options.storageBucket,
        fileName: testFileName,
        filePath: `_connection_tests/${testFileName}`,
        fileSize: uploadResult.metadata.size,
        contentType: uploadResult.metadata.contentType,
        downloadURL: downloadURL,
        operations: ['upload', 'getURL', 'delete']
      };

    } catch (storageError) {
      results.storage.success = false;
      results.storage.message = `Storage failed: ${storageError.message}`;
      results.storage.details = {
        code: storageError.code || 'unknown',
        message: storageError.message
      };
      console.error('❌ Storage test failed:', storageError);
    }

    // ============================================
    // OVERALL RESULTS
    // ============================================
    const allTestsPassed =
      results.initialization.success &&
      results.auth.success &&
      results.firestore.success &&
      results.storage.success;

    if (allTestsPassed) {
      results.overall.success = true;
      results.overall.message = '✅ All Firebase tests passed! Ready to set USE_FIREBASE = true';
      console.log('\n✅ ========================================');
      console.log('✅ ALL FIREBASE TESTS PASSED!');
      console.log('✅ ========================================\n');
    } else {
      results.overall.success = false;
      const failedTests = [];
      if (!results.initialization.success) failedTests.push('Initialization');
      if (!results.auth.success) failedTests.push('Auth');
      if (!results.firestore.success) failedTests.push('Firestore');
      if (!results.storage.success) failedTests.push('Storage');

      results.overall.message = `❌ Some tests failed: ${failedTests.join(', ')}`;
      console.log('\n❌ ========================================');
      console.log(`❌ TESTS FAILED: ${failedTests.join(', ')}`);
      console.log('❌ ========================================\n');
    }

  } catch (error) {
    results.overall.success = false;
    results.overall.message = `Unexpected error: ${error.message}`;
    console.error('❌ Unexpected error during Firebase tests:', error);
  }

  return results;
};

/**
 * Quick connection check (lighter version)
 * Just checks if Firebase is initialized and services are available
 */
export const quickConnectionCheck = () => {
  const status = {
    initialized: isFirebaseInitialized(),
    services: {
      auth: auth !== null,
      firestore: db !== null,
      storage: storage !== null
    }
  };

  const allServicesAvailable = status.initialized &&
    status.services.auth &&
    status.services.firestore &&
    status.services.storage;

  return {
    success: allServicesAvailable,
    message: allServicesAvailable
      ? 'Firebase ready'
      : 'Firebase not ready',
    ...status
  };
};

/**
 * Get current Firebase configuration (for debugging)
 * Note: Doesn't expose sensitive keys
 */
export const getFirebaseConfig = () => {
  if (!auth) {
    return { error: 'Firebase not initialized' };
  }

  return {
    projectId: auth.config.apiKey ? auth.config.apiKey.substring(0, 10) + '...' : 'N/A',
    authDomain: auth.config.authDomain || 'N/A',
    storageBucket: storage?.app?.options?.storageBucket || 'N/A',
    initialized: isFirebaseInitialized()
  };
};

export default runFirebaseTests;
